import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdAbstractSingleSelect } from './abstract-single-select';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  take,
  takeUntil,
} from 'rxjs';
import { BdUiSingleSelectConfig } from './single-select.config';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { IftaLabel } from 'primeng/iftalabel';
import { Select } from 'primeng/select';
import { Message } from 'primeng/message';
import { OverlayOptions } from 'primeng/api';
import { BdAbstractOption } from '../multi/multi-select.config';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';

@Component({
  selector: 'bd-ui-single-select',
  template: `
    <p-iftalabel>
      <p-select
        [inputId]="selectConfig.id"
        [formControl]="selectCtrl"
        [options]="allOptions | async"
        [data-test]="selectConfig.id"
        fluid
        variant="filled"
        [class]="
          valid
            ? 'mb-4'
            : errorMessage
              ? 'ng-invalid ng-dirty'
              : 'mb-4 ng-invalid ng-dirty'
        "
        [loading]="searching"
        [showClear]="selectConfig.resetOption"
        dataKey="dataKey"
        optionLabel="displayName"
        [filter]="showFilter"
        [filterFields]="['searchTerm']"
        filterBy="searchTerm"
        [filterPlaceholder]="placeholderLabel"
        [emptyFilterMessage]="noEntriesFoundLabel"
        [emptyMessage]="noEntriesFoundLabel"
        [overlayOptions]="overlayOptions"
        optionDisabled="disabled"
      >
      </p-select>
      <label [for]="selectConfig.id">{{ selectConfig?.selectLabel }}</label>
    </p-iftalabel>
    @if (errorMessage) {
      <p-message
        severity="error"
        variant="simple"
        size="small"
        icon="pi pi-exclamation-circle"
        [id]="selectConfig.id + '-help'"
      >
        {{ errorMessage }}
      </p-message>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IftaLabel,
    Select,
    AsyncPipe,
    BdDataTestDirective,
    Message,
  ],
})
export class BdUiSingleSelectComponent<TOption>
  extends BdAbstractSingleSelect<TOption, BdAbstractOption<TOption>>
  implements OnDestroy
{
  readonly config = input<BdUiSingleSelectConfig<TOption>>();
  readonly initialSelection = input<TOption | Observable<TOption> | undefined>(
    undefined,
  );
  readonly selectOptions = input<Observable<TOption[]>>(of([]));

  readonly selection = output<TOption | undefined>();
  readonly optionsChange = output<Array<TOption>>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public showFilter = false;
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private static readonly FILTER_THRESHOLD = 3;

  public selectConfig: Required<BdUiSingleSelectConfig<TOption>> = {
    id: 'single-select',
    selectLabel: 'Element',
    option: {
      uniqueFnc: () => '',
      sortFnc: () => 0,
      searchOptionAccessor: () => '',
      searchOptionDisplayName: () => '',
      disabledFnc: () => false,
    },
    emitEventOnInitialSelection: false,
    resetOption: false,
    valid$: new ReplaySubject<{
      isValid: boolean;
      errorMessage?: string;
    }>(1),
  };

  // Event subjects for coordinating initialization
  private _configReady$ = new ReplaySubject<void>(1);
  private _initialSelection$ = new ReplaySubject<TOption | undefined>(1);
  private _optionsLoaded$ = new ReplaySubject<void>(1);

  constructor() {
    super();

    // Initialize component when config and options are ready (does not wait for initial selection)
    combineLatest([this._configReady$, this._optionsLoaded$])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.init(this.selectConfig);
        this._cdr.markForCheck();
      });

    // Apply initial selection once a non-null value arrives AND config + options are ready.
    combineLatest([
      this._configReady$,
      this._optionsLoaded$,
      this._initialSelection$.pipe(
        filter((v): v is TOption => v !== undefined && v !== null),
        take(1),
        map((initialSelection) => this.transformToWrapper(initialSelection)),
      ),
    ])
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(([, , wrappedSelection]) => {
        this.setSelection(wrappedSelection, {
          emitEvent: this.selectConfig.emitEventOnInitialSelection,
        });
        this._cdr.markForCheck();
      });

    this.allOptions.pipe(takeUntil(this._onDestroy)).subscribe((options) => {
      this.showFilter =
        options.length > BdUiSingleSelectComponent.FILTER_THRESHOLD;
      this._cdr.markForCheck();
    });

    // React to config changes
    effect(() => {
      const config = this.config();
      if (!config) return;
      this.selectConfig = {
        id: `single-select-${config.selectLabel}`,
        emitEventOnInitialSelection: false,
        valid$: new ReplaySubject<{
          isValid: boolean;
          errorMessage?: string;
        }>(1),
        resetOption: false,
        ...config,
        option: {
          searchOptionDisplayName: (option) =>
            config.option.searchOptionDisplayName
              ? config.option.searchOptionDisplayName(option)
              : config.option.searchOptionAccessor(option),
          ...config.option,
        },
      };
      this._configReady$.next();
    });

    // React to initialSelection changes
    effect(() => {
      const value = this.initialSelection();
      if (value instanceof Observable) {
        value
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe((it) => this._initialSelection$.next(it));
      } else {
        this._initialSelection$.next(value);
      }
    });

    // React to selectOptions changes
    effect(() => {
      const options$ = this.selectOptions();
      this._loadOptions(options$);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected onSelectionChange(
    selection: BdAbstractOption<TOption> | null,
  ): void {
    if (selection) {
      this.selection.emit(selection.realValue);
    } else {
      this.selection.emit(undefined);
    }
  }

  protected transformToWrapper(option: TOption): BdAbstractOption<TOption> {
    const disabledFnc = this.selectConfig.option.disabledFnc;
    return {
      realValue: option,
      dataKey: this.selectConfig.option.uniqueFnc(option),
      searchTerm: this.selectConfig.option.searchOptionAccessor(option),
      displayName: this.selectConfig.option.searchOptionDisplayName(option),
      disabled: disabledFnc ? disabledFnc(option) : false,
    };
  }

  private _loadOptions(options$: Observable<TOption[]>): void {
    this.searching = true;

    options$
      .pipe(
        map((options) => options.map((it) => this.transformToWrapper(it))),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (availableOptions) => {
          // Sorting uses a consumer-provided comparator. Guard it so a faulty
          // sortFnc (e.g. comparing undefined option values) cannot throw before
          // `searching` is reset and leave the select stuck in a loading state.
          let sortedOptions = availableOptions.slice();
          try {
            sortedOptions = sortedOptions.sort((a, b) =>
              this.selectConfig.option.sortFnc(a.realValue, b.realValue),
            );
          } catch (err) {
            console.warn(
              `Sortierung von ${this.selectConfig.selectLabel} fehlgeschlagen`,
              err,
            );
          }
          this.allOptions.next(sortedOptions);
          this.searching = false;
          this.optionsChange.emit(availableOptions.map((it) => it.realValue));
          this._optionsLoaded$.next();
          this._cdr.markForCheck();
        },
        error: (err) => {
          console.warn(
            `Laden von ${this.selectConfig.selectLabel} nicht möglich`,
            err,
          );
          this.allOptions.next([]);
          this.searching = false;
          this.valid = false;
          this.errorMessage = 'Laden fehlgeschlagen';
          this._optionsLoaded$.next();
          this._cdr.markForCheck();
        },
      });
  }
}
